import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import MigrationNoteDTO from '@src/adapters/dto/migration/MigrationNoteDTO';
import { useSaveMigrationNoteLazy } from '@src/adapters/hooks/migration/useSaveMigrationNote';
import { Select, MenuItem } from '@mui/material';
import { MigrationNoteType } from '@src/constants/MigrationNoteType';

const PREFIX = 'MigrationNote';

const classes = {
  title: `${PREFIX}-title`,
  container: `${PREFIX}-container`,
  item: `${PREFIX}-item`,
  buttons: `${PREFIX}-buttons`,
  button: `${PREFIX}-button`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.title}`]: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  [`& .${classes.container}`]: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  [`& .${classes.item}`]: {
    width: '100%',
    marginTop: '20px',
    marginBottom: '20px',
  },
  [`& .${classes.buttons}`]: {
    flex: '1',
    marginRight: '8px',
  },
  [`& .${classes.button}`]: {
    flex: '1',
    marginLeft: '8px',
  },
}));

interface Props {
  close(note: MigrationNoteDTO | undefined): void;
  migrationId: string;
  note?: MigrationNoteDTO;
  isOpen: boolean;
  type?: MigrationNoteType;
}

function NoteDetailDialog({ close, migrationId, isOpen, note, type }: Props) {
  const { execute: saveNote } = useSaveMigrationNoteLazy();

  const fixedType = useMemo(() => {
    if (type) {
      return type;
    }

    if (note) {
      return note.type;
    }

    return undefined;
  }, [note, type]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<MigrationNoteDTO>();

  useEffect(() => {
    if (isOpen && !note && type) {
      reset({ type: type });
    } else {
      reset(note);
    }
  }, [isOpen, reset, note, type]);

  const onSubmit = async (data: MigrationNoteDTO) => {
    const result = await saveNote({
      note: data,
      migrationId: migrationId,
    });

    if (result.note) {
      reset();
      close(result.note);
    }
  };

  const handleClose = () => {
    reset();
    close(undefined);
  };

  return (
    <>
      <StyledDialog
        open={isOpen}
        aria-labelledby="Note Details"
        aria-describedby="Note Details"
        fullWidth={true}
        maxWidth={'md'}>
        <DialogTitle id="alert-dialog-title" className={classes.title}>
          <Typography variant="h6">
            {`${note ? 'Update' : 'Add'} ${fixedType ? ` ${fixedType} ` : ' '}
            Note`}
          </Typography>
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent className={classes.container}>
            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Content"
                  multiline
                  className={classes.item}
                />
              )}
            />
            {errors.content && <p>{errors.content.message}</p>}
            {!fixedType && (
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="Type" className={classes.item}>
                    <MenuItem value={MigrationNoteType.Scrub}>
                      Scrub Note
                    </MenuItem>
                    <MenuItem value={MigrationNoteType.General}>
                      General Note
                    </MenuItem>
                    <MenuItem value={MigrationNoteType.Migration}>
                      Migration Note
                    </MenuItem>
                  </Select>
                )}
              />
            )}
            {!fixedType && errors.type && <p>{errors.type.message}</p>}
          </DialogContent>
          <DialogActions className={classes.buttons}>
            <Button
              onClick={handleClose}
              color="primary"
              variant="outlined"
              className={classes.button}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              className={classes.button}>
              {note ? 'Save' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </StyledDialog>
    </>
  );
}

export default NoteDetailDialog;
